import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import { regions as regionsAPI, projects as projectsAPI } from "../../lib/database";
import RegionManagementPanel from "./components/RegionManagementPanel";

const RegionManagement = () => {
  const [regions, setRegions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [regionsData, projectsData] = await Promise.all([
        regionsAPI.getAll(),
        projectsAPI.getAll(),
      ]);
      setRegions(regionsData || []);
      setProjects(projectsData || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRegion = async (newRegion) => {
    try {
      setError(null);
      const createdRegion = await regionsAPI.create(newRegion);
      setRegions([createdRegion, ...regions]);
    } catch (err) {
      console.error("Error creating region:", err);
      setError(err.message || "Failed to create region");
      throw err;
    }
  };

  const handleUpdateRegion = async (regionId, updatedData) => {
    try {
      setError(null);
      const updatedRegion = await regionsAPI.update(regionId, updatedData);
      setRegions(
        regions.map((region) =>
          region.id === regionId ? updatedRegion : region
        )
      );
    } catch (err) {
      console.error("Error updating region:", err);
      setError(err.message || "Failed to update region");
      throw err;
    }
  };

  const handleDeleteRegion = async (regionId) => {
    try {
      setError(null);
      await regionsAPI.delete(regionId);
      setRegions(regions.filter((region) => region.id !== regionId));
    } catch (err) {
      console.error("Error deleting region:", err);
      setError(err.message || "Failed to delete region");
      throw err;
    }
  };

  return (
    <>
      <Helmet>
        <title>Region Management - Solar Installation Tracker</title>
        <meta
          name="description"
          content="Manage regions and view region assignments"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <main className="content-area">
          <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Region Management
              </h1>
              <p className="text-muted-foreground">
                Manage regions within projects and organize installation locations
              </p>
              {error && (
                <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading regions...</p>
              </div>
            ) : (
              <RegionManagementPanel
                regions={regions}
                projects={projects}
                onAddRegion={handleAddRegion}
                onUpdateRegion={handleUpdateRegion}
                onDeleteRegion={handleDeleteRegion}
              />
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default RegionManagement;
